import { useEffect } from 'react';
import { useAuthContext } from '../auth/useAuthContext';
import { useEditorContext } from './useEditorContext';
import { useEditorDomData } from './useEditorDomData';
import { SearchParam } from '../../utilities/constants/route';
import { useSearchParam } from '../useSearchParam';
import { PopupType } from '../../utilities/constants/popup-type';
import usePreAuthAction, { PreAuthAction } from '../usePreAuthAction';
import Popup from '../../services/popup/popup';
import { useError } from '../state/useError';
import { ErrorPhrase } from '../../utilities/constants/phrases';

const shouldGenerateText = (
  userRequest: string,
  userTextFromFiles: string[],
  generationChatId: string
) => (!!userRequest?.length || !!userTextFromFiles.length) && !generationChatId;

const CLEAR_PRE_AUTH_ACTION_DELAY = 3000;

interface IUseEditorGenerationOnRenderOptions {
  handleGenerationRequest: () => void;
  handleInitEditorPopups: (shouldOpenPopup: boolean) => void;
}

export const useEditorGenerationOnRender = (
  options: IUseEditorGenerationOnRenderOptions
) => {
  const { handleGenerationRequest, handleInitEditorPopups } = options;

  const {
    isInitialUserDataUpdated,
    isUserSubscribed,
    freePremiumGenerationsAttemptsCount,
  } = useAuthContext();

  const {
    userHomeworkHelpRequest,
    userHomeworkHelpTextFromFiles,
    userHomeworkHelpNextRequest,
    generationSubjectGroup,
    generationChatId,
    EditorActionCreator,
    editorDispatch,
  } = useEditorContext();

  const { isDomDataLoaded } = useEditorDomData();

  const { userPreAuthAction, clearPreAuthAction } = usePreAuthAction();

  const { setError } = useError();

  const showAiToolsSubscriptionPopup =
    userPreAuthAction === PreAuthAction.SHOW_AI_TOOLS_SUBSCRIPTION_POPUP;
  const isCopyTextAction = userPreAuthAction === PreAuthAction.COPY_TEXT_ACTION;
  const isDownloadTextAction =
    userPreAuthAction === PreAuthAction.DOWNLOAD_TEXT_ACTION;

  const {
    param: isIncomingRequest,
    deleteParamFromUrl: deleteParamIncomingRequestFromUrl,
  } = useSearchParam(SearchParam.INCOMING_REQUEST);
  const {
    param: isAuthSuccess,
    deleteParamFromUrl: deleteParamAuthSuccessFromUrl,
  } = useSearchParam(SearchParam.JUST_AUTH);
  const {
    param: isPaymentSuccess,
    deleteParamFromUrl: deleteParamPaymentSuccessFromUrl,
  } = useSearchParam(SearchParam.JUST_COME_AFTER_PAY);

  useEffect(() => {
    if (
      !isDomDataLoaded ||
      !isInitialUserDataUpdated ||
      showAiToolsSubscriptionPopup
    ) {
      return;
    }

    const shouldGenerateTextOnRender = shouldGenerateText(
      userHomeworkHelpRequest,
      userHomeworkHelpTextFromFiles,
      generationChatId
    );

    const shouldGenerateNextTextOnRender =
      !!userHomeworkHelpNextRequest?.length && !!generationChatId;

    handleInitEditorPopups(
      shouldGenerateTextOnRender || shouldGenerateNextTextOnRender
    );

    if (
      !shouldGenerateTextOnRender &&
      !shouldGenerateNextTextOnRender &&
      !generationChatId
    ) {
      setError(ErrorPhrase.NO_TEXT_TO_GENERATE);
    }

    if (
      (shouldGenerateTextOnRender || shouldGenerateNextTextOnRender) &&
      (isUserSubscribed || freePremiumGenerationsAttemptsCount > 0)
    ) {
      handleGenerationRequest();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialUserDataUpdated, isDomDataLoaded]);

  useEffect(() => {
    if (isIncomingRequest) {
      editorDispatch(EditorActionCreator.setDefaultGenerationState());
      deleteParamIncomingRequestFromUrl();
    }
  }, [
    isIncomingRequest,
    EditorActionCreator,
    editorDispatch,
    deleteParamIncomingRequestFromUrl,
  ]);

  useEffect(() => {
    if (!isInitialUserDataUpdated) {
      return;
    }

    if (showAiToolsSubscriptionPopup && !isUserSubscribed) {
      Popup.show(PopupType.AI_TOOLS_SUBSCRIPTION, {
        subjectGroup: generationSubjectGroup,
      });
    }

    setTimeout(() => clearPreAuthAction(), CLEAR_PRE_AUTH_ACTION_DELAY);

    if (isCopyTextAction || isDownloadTextAction) {
      clearPreAuthAction();
    }
  }, [
    showAiToolsSubscriptionPopup,
    clearPreAuthAction,
    isInitialUserDataUpdated,
    isUserSubscribed,
    isCopyTextAction,
    isDownloadTextAction,
    generationSubjectGroup,
  ]);

  useEffect(() => {
    if (
      isCopyTextAction ||
      isDownloadTextAction ||
      !isInitialUserDataUpdated ||
      !isUserSubscribed
    ) {
      return;
    }

    if (isAuthSuccess || isPaymentSuccess) {
      if (isInitialUserDataUpdated && isUserSubscribed && generationChatId) {
        editorDispatch(EditorActionCreator.setDefaultGenerationState());
        return;
      }

      const shouldGenerateTextOnRender = shouldGenerateText(
        userHomeworkHelpRequest,
        userHomeworkHelpTextFromFiles,
        generationChatId
      );

      if (shouldGenerateTextOnRender) {
        handleInitEditorPopups(true);
        handleGenerationRequest();
      }

      deleteParamAuthSuccessFromUrl();
      deleteParamPaymentSuccessFromUrl();
      clearPreAuthAction();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitialUserDataUpdated, isUserSubscribed, generationChatId]);
};
